import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserX, TriangleAlert } from 'lucide-react';
import { ROLE_SEEDS } from '@cumpli/shared';
import { api, RequestError } from '../lib/api';
import { useMe, useRoles, useUser } from '../lib/hooks';
import { useToast } from '../lib/use-toast';
import { resourceViewState } from '../lib/view-state';
import {
  Button,
  Card,
  Dialog,
  EmptyState,
  FieldError,
  Input,
  Label,
  PageHeader,
  Pill,
  Skeleton,
} from '../components/ui';

const roleDescription = (key: string) => ROLE_SEEDS.find((r) => r.key === key)?.description;

interface FormValues {
  fullName: string;
  roleKeys: string[];
}

export default function UserEdit() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: user, isLoading, isError, error } = useUser(id);
  const view = resourceViewState({ isLoading, isError, error, data: user });
  const { data: roles } = useRoles();
  const { data: me } = useMe();
  const isTenantAdmin = (me?.roles ?? []).includes('tenant_admin');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: { fullName: '', roleKeys: [] } });

  useEffect(() => {
    if (user) reset({ fullName: user.fullName, roleKeys: user.roles });
  }, [user, reset]);

  const save = useMutation({
    mutationFn: async (data: FormValues) => {
      await api.updateUser(id!, { fullName: data.fullName });
      await api.assignRoles(id!, data.roleKeys);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({ message: t('users.updated'), kind: 'success' });
      navigate('/usuarios');
    },
    onError: () => toast({ message: t('common.genericError'), kind: 'error' }),
  });

  const deactivate = useMutation({
    mutationFn: () => api.deactivateUser(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({ message: t('users.deactivated_toast'), kind: 'success' });
      navigate('/usuarios');
    },
    onError: () => toast({ message: t('common.genericError'), kind: 'error' }),
  });

  const reactivate = useMutation({
    mutationFn: () => api.reactivateUser(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({ message: t('users.reactivated_toast'), kind: 'success' });
      navigate('/usuarios');
    },
    onError: () => toast({ message: t('common.genericError'), kind: 'error' }),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteUser(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({ message: t('users.deleted_toast'), kind: 'success' });
      navigate('/usuarios');
    },
    onError: (err) => {
      // The server enforces the same rule the UI does: a user must be
      // deactivated before it can be deleted (409 MUST_DEACTIVATE_FIRST).
      const mustDeactivate =
        err instanceof RequestError && err.code === 'MUST_DEACTIVATE_FIRST';
      setDeleteOpen(false);
      toast({
        message: mustDeactivate
          ? t('users.mustDeactivateFirst')
          : t('common.genericError'),
        kind: 'error',
      });
    },
  });

  if (view === 'loading') {
    return (
      <div>
        <PageHeader title={t('users.editTitle')} />
        <Card className="max-w-lg">
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-10 w-40" />
          </div>
        </Card>
      </div>
    );
  }

  if (view === 'notFound') {
    return (
      <div>
        <PageHeader title={t('users.editTitle')} />
        <Card className="max-w-lg">
          <EmptyState
            icon={<UserX className="h-8 w-8" strokeWidth={1.5} />}
            title={t('users.notFoundTitle')}
            hint={t('users.notFoundHint')}
            action={
              <Button variant="outline" onClick={() => navigate('/usuarios')}>
                {t('users.backToUsers')}
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  if (view === 'error') {
    return (
      <div>
        <PageHeader title={t('users.editTitle')} />
        <Card className="max-w-lg">
          <EmptyState
            icon={<TriangleAlert className="h-8 w-8" strokeWidth={1.5} />}
            title={t('users.loadErrorTitle')}
            hint={t('users.loadErrorHint')}
            action={
              <Button variant="outline" onClick={() => navigate('/usuarios')}>
                {t('users.backToUsers')}
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div>
      <PageHeader
        title={t('users.editTitle')}
        subtitle={user.email}
        actions={<Pill tone={user.status === 'active' ? 'green' : 'slate'}>{user.status === 'active' ? t('users.active') : t('users.deactivated')}</Pill>}
      />
      <Card className="max-w-lg">
      <form
        onSubmit={handleSubmit((data) => save.mutate(data))}
        className="space-y-4"
      >
        <div>
          <Label htmlFor="fullName">{t('users.fullName')}</Label>
          <Input
            id="fullName"
            {...register('fullName', { required: true, minLength: 2, maxLength: 120 })}
          />
          <FieldError message={errors.fullName && t('users.nameLength')} />
        </div>
        <fieldset>
          <Label>{t('users.roles')}</Label>
          <div className="space-y-2">
            {roles?.map((r) => {
              const description = roleDescription(r.key);
              return (
                <label
                  key={r.key}
                  className="flex gap-2.5 rounded-xl border border-line bg-panel p-3 text-sm transition-colors hover:border-ink-faint"
                >
                  <input
                    type="checkbox"
                    value={r.key}
                    {...register('roleKeys', { required: true })}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                  />
                  <span>
                    <span className="font-medium text-ink">{r.name}</span>
                    {description && (
                      <span className="mt-0.5 block text-xs leading-snug text-ink-soft">
                        {description}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
          <FieldError message={errors.roleKeys && t('users.rolesRequired')} />
        </fieldset>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-3">
            <Button type="submit" disabled={isSubmitting || save.isPending}>
              {t('common.save')}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate('/usuarios')}>
              {t('common.cancel')}
            </Button>
          </div>
          {user.status === 'active' ? (
            <Button
              type="button"
              variant="destructive"
              className="whitespace-nowrap"
              onClick={() => setConfirmOpen(true)}
            >
              {t('users.deactivate')}
            </Button>
          ) : (
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="whitespace-nowrap"
                disabled={reactivate.isPending}
                onClick={() => reactivate.mutate()}
              >
                {t('users.reactivate')}
              </Button>
              {/* Deleting is destructive and only offered to tenant admins for an
                  already-deactivated user; the server enforces both rules too. */}
              {isTenantAdmin && (
                <Button
                  type="button"
                  variant="destructive"
                  className="whitespace-nowrap"
                  onClick={() => setDeleteOpen(true)}
                >
                  {t('users.delete')}
                </Button>
              )}
            </div>
          )}
        </div>
      </form>

      <Dialog
        open={confirmOpen}
        title={t('users.deactivate')}
        onClose={() => setConfirmOpen(false)}
      >
        <p className="mb-4 text-sm text-ink-soft">
          {t('users.deactivateConfirm', { name: user.fullName })}
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => setConfirmOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            disabled={deactivate.isPending}
            onClick={() => deactivate.mutate()}
          >
            {t('common.confirm')}
          </Button>
        </div>
      </Dialog>

      <Dialog open={deleteOpen} title={t('users.deleteTitle')} onClose={() => setDeleteOpen(false)}>
        <p className="mb-4 text-sm text-ink-soft">
          {t('users.deleteConfirmPrefix')}{' '}
          <strong className="text-ink">{user.fullName}</strong>{' '}
          {t('users.deleteConfirmSuffix')}
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => setDeleteOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          >
            {t('users.delete')}
          </Button>
        </div>
      </Dialog>
      </Card>
    </div>
  );
}
